export interface DateRangeFilterValue {
  from: Date | null;
  to: Date | null;
  presetDays: number | null;
}

// Persisted filters round-trip through JSON, so dates may arrive as strings.
export interface DateRangeFilterInput {
  from?: Date | string | null;
  to?: Date | string | null;
  presetDays?: number | null;
}

export const EMPTY_DATE_RANGE: DateRangeFilterValue = { from: null, to: null, presetDays: null };

export const DEFAULT_MAX_DAYS_BACK = 30;

export function startOfDayDaysAgo(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function presetRange(days: number): DateRangeFilterValue {
  return { from: startOfDayDaysAgo(days), to: new Date(), presetDays: days };
}

// Presets carry identity (days), not dates: a saved "1 Week" re-anchors to today
// instead of drifting into the past. Absolute ranges clamp to the maxDaysBack floor.
export function normalizeDateRange(
  value: DateRangeFilterInput | null | undefined,
  maxDaysBack: number = DEFAULT_MAX_DAYS_BACK
): DateRangeFilterValue {
  if (!value) return EMPTY_DATE_RANGE;
  const from = toDate(value.from);
  const to = toDate(value.to);
  const presetDays = value.presetDays ?? null;

  if (presetDays != null) {
    const anchor = startOfDayDaysAgo(presetDays);
    return from && to && from.getTime() === anchor.getTime()
      ? { from, to, presetDays }
      : presetRange(presetDays);
  }

  const floor = startOfDayDaysAgo(maxDaysBack);
  return { from: from && from < floor ? floor : from, to, presetDays: null };
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  return value instanceof Date ? value : new Date(value);
}
